import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(t("login.welcome"));
      navigate(u.role === "admin" ? "/admin" : "/espace");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || t("login.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden flex-col justify-between bg-black p-12 text-white lg:flex">
        <div className="flex items-center justify-between">
          <Logo light />
          <LanguageSwitcher className="!border-white/30 !text-white/80 hover:!border-white hover:!text-white" />
        </div>
        <div className="ch-fade-up">
          <h1 className="font-display text-5xl font-extrabold leading-[1.05] tracking-tight xl:text-6xl">
            {t("login.brand_l1")}<br />{t("login.brand_l2")}<br />{t("login.brand_l3")}
          </h1>
          <p className="mt-6 max-w-sm text-base text-white/60">{t("login.brand_sub")}</p>
        </div>
        <p className="text-xs tracking-widest text-white/40">© {new Date().getFullYear()} CH CREATIVE</p>
      </div>

      <div className="flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-sm ch-fade-up">
          <div className="mb-10 flex items-center justify-between lg:hidden">
            <Logo />
            <LanguageSwitcher />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-400">{t("login.eyebrow")}</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight">{t("login.title")}</h2>
          <p className="mt-2 text-sm text-neutral-500">{t("login.subtitle")}</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">{t("common.email")}</Label>
              <Input id="email" data-testid="login-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder={t("login.email_ph")}
                className="h-11 rounded-sm" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("common.password")}</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-neutral-500 hover:text-black hover:underline">
                  {t("login.forgot")}
                </Link>
              </div>
              <Input id="password" data-testid="login-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="h-11 rounded-sm" />
            </div>
            <Button type="submit" data-testid="login-submit-button" disabled={loading}
              className="group h-11 w-full rounded-sm bg-black text-white transition-colors hover:bg-neutral-800">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <span className="flex items-center justify-center gap-2">
                  {t("login.submit")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
